import { Application, Container, Graphics, Text } from "pixi.js";
import { slotLabel } from "@scb/protocol";
import { DEFAULT_TABLE, fanGeometry, type TableSpec } from "@scb/sim";
import { replayDurationMs, replaySpeed } from "@scb/client-core";

export interface RenderBall {
  id: number;
  team: number;
  x: number;
  y: number;
  state: string;
}

/** 이번에 쏠 사람의 공(+조준선) 표시. 내 것일 수도, 상대 것일 수도 있다. */
export interface AimView {
  /** 발사 지점 (월드 좌표) */
  x: number;
  y: number;
  team: number;
  /** 조준 각도 (rad). null이면 아직 모른다 → 대기 공만 그린다. */
  angle: number | null;
  /** 내 조준이면 true — 강조색으로 그린다. */
  own: boolean;
}

/** 조준선 길이 (m). 세기와 무관하게 항상 이 길이다. */
const AIM_LINE_LENGTH = 0.55;

/**
 * 구조물은 **무채색**, 공만 색을 갖는다.
 *
 * 예전엔 빗이 파란 계열(0x6f86c4)이었는데 파란 공(0x3d8bff)이 그 위에 안착하면
 * 거의 구분이 안 됐다. 득점 여부를 한눈에 읽어야 하는 게임이라 치명적이다.
 * 그래서 빗·턱·격벽을 회색으로 내리고 색상환은 두 팀에게만 내줬다.
 */
const COLORS = {
  felt: 0x14203a,
  edge: 0x3a4a78,
  playLine: 0xffcc55,
  /** 판(부채꼴 반원판) 몸통 — 실물처럼 밝은 무채색 */
  plate: 0xd9dde8,
  plateEdge: 0x8b93a8,
  /** 페그 (슬롯을 나누는 말뚝) */
  divider: 0x7c8497,
  label: 0x8b96bd,
  /** 판 위에 적는 칸 이름 */
  slotLabel: 0x4a5470,
  /**
   * 팀 색 — 주황 / 청록.
   *
   * 색상환 반대편이라 서로 헷갈리지 않고, 적록색약에서도 구분된다
   * (파랑-주황 계열은 가장 안전한 대비 쌍이다). 회색 빗·남색 펠트·노란 Play Line
   * 어느 것과도 겹치지 않는다.
   */
  team: [0xff8c42, 0x36cfe8],
  aim: 0xffcc55,
} as const;

const PADDING = 28;
/**
 * 이 폭 아래로는 여백을 줄인다.
 *
 * 28px 여백은 넓은 화면에서는 티가 안 나지만, 390px 폰에서는 좌우 합 56px —
 * 폭의 14%다. 테이블 크기는 `min(폭기준, 높이기준)`에서 **폭 쪽이 결정**하므로
 * 그 56px이 그대로 테이블 크기를 깎는다. 판 이름표는 빗 바깥 여유 안에 그려져서
 * 여백이 줄어도 잘리지 않는다.
 */
const NARROW_WIDTH = 560;
const NARROW_PADDING = 12;

/** 판별 기본 점수 (docs/01-game-rules.md §3). 라벨 표시용. */
const POINTS: Record<number, number> = { 1: 1, 2: 2, 3: 5 };

export class TableRenderer {
  readonly app = new Application();
  private table: TableSpec;

  private world = new Container();
  private staticLayer = new Graphics();
  private labelLayer = new Container();
  private ballLayer = new Container();
  private aimLayer = new Graphics();

  private ballGfx = new Map<number, Graphics>();
  private base = new Map<number, RenderBall>();

  private scale = 100;
  private offsetX = 0;
  private offsetY = 0;

  /** 내 자리의 고정 발사 지점 (x). null이면 자리 고정 없음 (연습 모드). */
  private seatX: number | null = null;

  /**
   * 결과를 감춰야 하는 동안 true — 발사 순간부터 재생이 끝날 때까지.
   *
   * 공 그림은 **권위 상태**의 `state`를 쓰기 때문에, 그대로 두면 아직 굴러가는
   * 중인 공이 이미 `grouped`(득점 실패)로 반투명하게 그려져 결과가 먼저 새어 나간다.
   * `isReplaying`으로는 부족하다 — 세션이 `ShotResult`를 보내기 전에 상태를 먼저
   * 동기화해서, 재생이 시작되기 전에 이미 새 상태가 그려지기 때문이다.
   */
  private hideOutcome = false;

  // 리플레이 재생 상태
  private replay: number[][] | null = null;
  private replayHz = 30;
  private replayElapsed = 0;
  /** 긴 샷을 상한 안에 밀어넣기 위한 배속 */
  private replayRate = 1;
  private replayTeam = 0;
  private replayDone: (() => void) | null = null;
  /**
   * 워치독. 브라우저는 백그라운드 탭의 requestAnimationFrame을 멈추므로
   * Pixi 티커도 함께 멈춘다. 그러면 리플레이가 영원히 끝나지 않고,
   * 재생이 끝날 때까지 미뤄둔 권위 상태가 절대 반영되지 않는다.
   * setTimeout은 백그라운드에서도(느리게라도) 돌기 때문에 안전망이 된다.
   */
  private replayWatchdog: ReturnType<typeof setTimeout> | null = null;

  constructor(table: TableSpec = DEFAULT_TABLE) {
    this.table = table;
  }

  async init(host: HTMLElement): Promise<void> {
    await this.app.init({
      background: 0x0b1020,
      antialias: true,
      resizeTo: host,
      resolution: Math.min(window.devicePixelRatio ?? 1, 2),
      autoDensity: true,
    });
    host.appendChild(this.app.canvas);

    this.world.addChild(this.staticLayer, this.labelLayer, this.ballLayer, this.aimLayer);
    this.app.stage.addChild(this.world);

    this.layout();
    this.app.renderer.on("resize", () => this.layout());
    this.app.ticker.add(({ deltaMS }) => this.tick(deltaMS));
  }

  // ── 좌표 변환 ────────────────────────────────────────
  // 월드: y가 클수록 먼 쪽(업슬로프). 화면: y가 클수록 아래.

  private sx(x: number): number {
    return this.offsetX + x * this.scale;
  }

  private sy(y: number): number {
    return this.offsetY + (this.table.length - y) * this.scale;
  }

  /** 화면 좌표 → 월드 좌표 (조준 클릭 처리용) */
  toWorld(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.app.canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    return {
      x: (px - this.offsetX) / this.scale,
      y: this.table.length - (py - this.offsetY) / this.scale,
    };
  }

  /**
   * 캔버스를 host 크기에 다시 맞춘다.
   *
   * Pixi의 `resizeTo`는 **window resize만** 듣는다. 컴팩트 모드에서 하단 독의
   * 높이가 바뀌면(위치 슬라이더가 숨겨지는 등) `#stage`가 줄었는데도 캔버스는
   * 그대로 남아 테이블이 독 밑으로 밀려 들어간다. 그때 이걸 부른다.
   *
   * 초기화 전에는 renderer가 없으므로 조용히 넘어간다 — 어차피 init()이 layout()을 한다.
   */
  resize(): void {
    if (!this.app.renderer) return;
    this.app.resize();
  }

  private layout(): void {
    const w = this.app.renderer.width;
    const h = this.app.renderer.height;
    const pad = w < NARROW_WIDTH ? NARROW_PADDING : PADDING;
    this.scale = Math.min((w - pad * 2) / this.table.width, (h - pad * 2) / this.table.length);
    this.offsetX = (w - this.table.width * this.scale) / 2;
    this.offsetY = (h - this.table.length * this.scale) / 2;
    this.drawStatic();
    this.redrawBalls();
  }

  private drawStatic(): void {
    const g = this.staticLayer;
    const t = this.table;
    g.clear();

    // 테이블 전체가 균일한 경사다 (멈춰 서는 평지 구역은 없다).
    g.rect(this.sx(0), this.sy(t.length), t.width * this.scale, t.length * this.scale)
      .fill(COLORS.felt)
      .stroke({ width: 2, color: COLORS.edge });

    // Play Line — 이 아래에서만 발사 가능
    g.moveTo(this.sx(0), this.sy(t.playLineY))
      .lineTo(this.sx(t.width), this.sy(t.playLineY))
      .stroke({ width: 2, color: COLORS.playLine, alpha: 0.8 });

    // 자리 고정 대전 — 내 자리를 Play Line 위의 한 점으로 찍는다.
    // 위치를 못 바꾸므로 "구간"이 아니라 "점"이어야 오해가 없다.
    if (this.seatX !== null) {
      const sx = this.sx(this.seatX);
      const sy = this.sy(t.playLineY);
      g.circle(sx, sy, 7).stroke({ width: 2, color: COLORS.playLine, alpha: 0.95 });
      g.circle(sx, sy, 2.5).fill({ color: COLORS.playLine, alpha: 0.95 });
    }

    // Astrolabe — 부채꼴 반원판. 호가 먼 쪽(화면 위)으로 볼록하고,
    // 호를 따라 박힌 페그 사이가 슬롯이다. 실물 사진과 같은 형태.
    this.labelLayer.removeChildren().forEach((child) => child.destroy());

    for (const spec of t.astrolabes) {
      const fan = fanGeometry(spec, t.ballRadius);
      const cx = this.sx(fan.center.x);
      const cy = this.sy(fan.center.y);
      const rr = fan.radius * this.scale;
      const ri = fan.innerRadius * this.scale;

      // 판 몸통 — 부채 모양 띠. 월드 각도 α(+y 기준)는 화면 각도 α − 90°가 된다
      // (화면 y가 아래로 증가). 바깥 호를 그리고 안쪽 호로 되돌아와 닫는다.
      const t1 = -Math.PI / 2 - fan.sideAngle;
      const t2 = -Math.PI / 2 + fan.sideAngle;
      g.moveTo(cx + rr * Math.cos(t1), cy + rr * Math.sin(t1))
        .arc(cx, cy, rr, t1, t2)
        .lineTo(cx + ri * Math.cos(t2), cy + ri * Math.sin(t2))
        .arc(cx, cy, ri, t2, t1, true)
        .closePath()
        .fill(COLORS.plate)
        .stroke({ width: 1.5, color: COLORS.plateEdge });

      // 페그 — 호에 박혀 슬롯을 나눈다. 여기 맞으면 튕긴다.
      for (const peg of fan.pegs) {
        g.circle(this.sx(peg.x), this.sy(peg.y), fan.pegRadius * this.scale).fill(
          COLORS.divider,
        );
      }

      // 판 이름 + 점수 (판 왼쪽 옆)
      const plate = new Text({
        text: `${spec.id}번 ${POINTS[spec.id]}점`,
        style: { fontSize: 11, fill: COLORS.label, fontFamily: "sans-serif" },
      });
      plate.anchor.set(1, 0.5);
      plate.position.set(
        this.sx(fan.band.minX) - 8,
        this.sy(spec.center.y + (spec.radius + spec.innerRadius) / 2),
      );
      this.labelLayer.addChild(plate);

      // 칸 이름 (가운데 A, 양옆으로 B → C → D) — 실물처럼 판 위 호 안쪽에 적는다.
      for (let i = 0; i < spec.slotCount; i++) {
        const slot = fan.slots[i];
        const dx = slot.x - fan.center.x;
        const dy = slot.y - fan.center.y;
        const len = Math.hypot(dx, dy) || 1;
        const inner = fan.radius - 0.045;
        const name = new Text({
          text: slotLabel(spec.slotCount, i),
          style: { fontSize: 9, fill: COLORS.slotLabel, fontFamily: "sans-serif" },
        });
        name.anchor.set(0.5, 0.5);
        name.position.set(
          this.sx(fan.center.x + (dx / len) * inner),
          this.sy(fan.center.y + (dy / len) * inner),
        );
        this.labelLayer.addChild(name);
      }
    }
  }

  /**
   * 내 자리를 표시한다. null이면 자리 고정 없음(연습 모드).
   * 매 상태 변경마다 불리므로 실제로 바뀔 때만 다시 그린다 — drawStatic은
   * 라벨 Text를 전부 재생성해서 싸지 않다.
   */
  setSeat(x: number | null): void {
    if (this.seatX === x) return;
    this.seatX = x;
    this.drawStatic();
  }

  /** 결과 표시(득점 실패 반투명)를 감출지. main.ts의 `shotInFlight`와 수명이 같다. */
  setHideOutcome(hide: boolean): void {
    if (this.hideOutcome === hide) return;
    this.hideOutcome = hide;
    // 재생 중이면 다음 틱이 어차피 다시 그린다.
    if (!this.replay) this.redrawBalls();
  }

  // ── 공 ──────────────────────────────────────────────

  setBalls(balls: readonly RenderBall[]): void {
    this.base.clear();
    for (const b of balls) this.base.set(b.id, { ...b });
    if (!this.replay) this.redrawBalls();
  }

  private redrawBalls(positions?: Map<number, { x: number; y: number }>): void {
    const seen = new Set<number>();
    const r = this.table.ballRadius * this.scale;

    const draw = (ball: RenderBall, x: number, y: number) => {
      seen.add(ball.id);
      let g = this.ballGfx.get(ball.id);
      if (!g) {
        g = new Graphics();
        this.ballGfx.set(ball.id, g);
        this.ballLayer.addChild(g);
      }
      const color = COLORS.team[ball.team === 1 ? 1 : 0];
      g.clear();
      // 결과를 드러내는 표시는 전부 재생이 끝난 뒤로 미룬다:
      //   · 안착(captured) 테두리 — 아예 없앴다
      //   · 득점 실패(grouped) 반투명 — 굴러가는 동안에는 불투명하게 둔다
      // 둘 다 권위 상태를 그대로 그리는 바람에 "이 공은 들어갔다/실패했다"가
      // 공이 멈추기도 전에 보였다.
      const failed = !this.hideOutcome && ball.state === "grouped";
      g.circle(0, 0, r).fill({ color, alpha: failed ? 0.55 : 1 });
      g.position.set(this.sx(x), this.sy(y));
    };

    for (const ball of this.base.values()) {
      const override = positions?.get(ball.id);
      draw(ball, override?.x ?? ball.x, override?.y ?? ball.y);
    }

    // 리플레이 중에만 존재하는 공 (아직 스키마에 안 들어온 새 공)
    if (positions) {
      for (const [id, p] of positions) {
        if (this.base.has(id)) continue;
        draw({ id, team: this.replayTeam, x: p.x, y: p.y, state: "in_play" }, p.x, p.y);
      }
    }

    for (const [id, g] of this.ballGfx) {
      if (seen.has(id)) continue;
      g.destroy();
      this.ballGfx.delete(id);
    }
  }

  // ── 조준 ────────────────────────────────────────────

  /**
   * 자리에 놓인 공들과 조준선.
   *
   * 대전에서는 **양쪽 자리가 항상 보인다** — 자리가 고정이라 위치는 어차피 공개
   * 정보고, 상대가 안 보이면 내 턴만 덩그러니 남아 대전 같지 않다.
   *
   * `angle`이 null이면 공만 그린다. 상대가 아직 안 쐈으면 각도를 알 수 없기
   * 때문이다 — 서버가 샷 결과와 함께 알려주기 전까지는 알 방법이 없고,
   * 미리 보여줘서도 안 된다.
   */
  showAim(views: readonly AimView[]): void {
    const g = this.aimLayer;
    g.clear();

    const r = this.table.ballRadius * this.scale;

    for (const view of views) {
      // 공은 언제나 팀 색 — 누가 어느 자리인지 색으로 바로 읽힌다.
      const ballColor = COLORS.team[view.team === 1 ? 1 : 0];
      const cx = this.sx(view.x);
      const cy = this.sy(view.y);

      // 테이블 위의 실제 공과 헷갈리지 않게 반투명 + 테두리로 그린다.
      g.circle(cx, cy, r).fill({ color: ballColor, alpha: 0.3 });
      g.circle(cx, cy, r).stroke({ width: 1.5, color: ballColor, alpha: 0.95 });

      if (view.angle === null) continue;

      // 길이는 **항상 같다**. 예전엔 세기에 비례해 늘어났는데, 조준선이 궤적처럼
      // 보여서 "선 끝까지 굴러간다"는 오해를 부르고 각도를 읽기도 어려웠다.
      // 세기는 세기 게이지가 따로 보여준다. 조준선은 방향만 가리킨다.
      const dir = { x: Math.sin(view.angle), y: Math.cos(view.angle) };
      // 내 조준선만 강조색(노랑) — 내가 지금 겨누는 선이 한눈에 구분돼야 한다.
      const lineColor = view.own ? COLORS.aim : ballColor;

      g.moveTo(cx, cy)
        .lineTo(
          this.sx(view.x + dir.x * AIM_LINE_LENGTH),
          this.sy(view.y + dir.y * AIM_LINE_LENGTH),
        )
        .stroke({ width: 2, color: lineColor, alpha: 0.75 });
    }
  }

  clearAim(): void {
    this.aimLayer.clear();
  }

  // ── 리플레이 ────────────────────────────────────────

  playReplay(frames: number[][], sampleHz: number, shooterTeam: number, onDone: () => void): void {
    // 이전 리플레이가 아직 안 끝났으면 강제로 마무리해서 콜백이 유실되지 않게 한다.
    this.finishReplay();

    if (frames.length === 0) {
      onDone();
      return;
    }
    this.replay = frames;
    this.replayHz = sampleHz;
    this.replayElapsed = 0;
    this.replayRate = replaySpeed(frames.length, sampleHz);
    this.replayTeam = shooterTeam;
    this.replayDone = onDone;
    this.clearAim();

    this.replayWatchdog = setTimeout(
      () => this.finishReplay(),
      replayDurationMs(frames.length, sampleHz) + 800,
    );
  }

  get isReplaying(): boolean {
    return this.replay !== null;
  }

  /** 재생을 끝내고 마지막 프레임으로 스냅한 뒤 콜백을 호출한다. 여러 번 불려도 안전하다. */
  private finishReplay(): void {
    if (this.replayWatchdog !== null) {
      clearTimeout(this.replayWatchdog);
      this.replayWatchdog = null;
    }
    if (!this.replay) return;

    this.replay = null;
    const done = this.replayDone;
    this.replayDone = null;
    // 마지막 프레임이 아니라 **권위 상태(base)** 로 그린다.
    // 프레임에는 이미 사라진 공(Gutter)이 남아 있을 수 있어서 유령이 생긴다.
    this.redrawBalls();
    done?.();
  }

  private tick(deltaMS: number): void {
    const frames = this.replay;
    if (!frames) return;

    // 탭이 백그라운드였다가 돌아오면 deltaMS가 수 초로 튈 수 있다. 한 프레임 분량으로 제한.
    this.replayElapsed += (Math.min(deltaMS, 100) * this.replayRate) / 1000;
    const pos = this.replayElapsed * this.replayHz;
    const i = Math.floor(pos);

    if (i >= frames.length - 1) {
      this.finishReplay();
      return;
    }

    // 프레임 사이를 보간해서 30Hz 샘플을 부드럽게 재생한다.
    const a = frameToMap(frames[i]);
    const b = frameToMap(frames[i + 1]);
    const t = pos - i;
    const merged = new Map<number, { x: number; y: number }>();

    for (const [id, pa] of a) {
      const pb = b.get(id);
      merged.set(
        id,
        pb ? { x: pa.x + (pb.x - pa.x) * t, y: pa.y + (pb.y - pa.y) * t } : pa,
      );
    }
    for (const [id, pb] of b) {
      if (!merged.has(id)) merged.set(id, pb);
    }

    this.redrawBalls(merged);
  }
}

function frameToMap(frame: number[] | undefined): Map<number, { x: number; y: number }> {
  const map = new Map<number, { x: number; y: number }>();
  if (!frame) return map;
  for (let i = 0; i + 2 < frame.length; i += 3) {
    map.set(frame[i], { x: frame[i + 1], y: frame[i + 2] });
  }
  return map;
}
